import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { pushChannelCopy } from "~/components/notifications/channel-copy";
import { ReminderTimeField } from "~/components/routine/ReminderTimeField";
import { SettingsPage } from "~/components/settings/SettingsPage";
import { useProfileSave } from "~/components/settings/use-profile-save";
import { onResume } from "~/lib/capacitor";
import {
  ensureNotificationPermission,
  type NotificationPermissionState,
} from "~/lib/push-notifications";
import {
  reminderSettings,
  type RemindersLinkState,
  reminderUpdate,
  ROUTINE_PATH,
  ROUTINE_SLOTS,
} from "~/lib/routine";
import {
  reminderPermissionState,
  requestReminderPermission,
  resyncRoutineReminders,
} from "~/lib/routine-reminders";
import { useAuthStore } from "~/store/auth-store";
import type { RoutineSlot } from "~/types/routine";

/**
 * Morning and Evening Elyxeer — when the phone should remind the member.
 *
 * Each reminder is a switch, and once it is on, a time. Both save as they are changed,
 * like the notification switches: this screen has no Save button to forget. Turning a
 * reminder on is also the moment to ask the OS for permission — the member has just
 * said yes to exactly what the dialog asks about — and a phone that refuses is said so
 * plainly, because a switch reading "on" over a reminder that can never ring is the one
 * outcome the member cannot diagnose from inside the app.
 *
 * Reachable from the profile menu and from the routine screen; "back" returns to
 * whichever one opened it.
 */
export function RemindersRoute() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const { save, saving, status, dismiss } = useProfileSave();
  const [permission, setPermission] =
    useState<NotificationPermissionState | null>(null);

  useEffect(() => {
    let cancelled = false;
    const read = () =>
      void reminderPermissionState().then((state) => {
        if (!cancelled) setPermission(state);
      });
    read();
    // Coming back from the phone's settings is how a "no" becomes a "yes".
    const stop = onResume(read);
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  if (!user) return null;

  const reminders = reminderSettings(user);
  const fromRoutine =
    (location.state as RemindersLinkState | null)?.from === "routine";

  const toggle = async (slot: RoutineSlot, enabled: boolean) => {
    await save(reminderUpdate(slot, { enabled }));
    if (!enabled) return;
    // The push path first, so the device is registered for the webinar pushes the same
    // grant covers; then the local one, which also covers a phone with no FCM at all.
    // Neither failure may undo the switch — the account setting is what every other
    // device of this member follows.
    try {
      await ensureNotificationPermission({
        userId: user.id,
        channels: pushChannelCopy(t),
      });
    } catch {
      // The line below re-reads the real state either way.
    }
    try {
      await requestReminderPermission();
    } catch {
      // Likewise.
    }
    setPermission(await reminderPermissionState());
    // The sync that followed the save skipped scheduling while permission was
    // unanswered. It may not be any more.
    void resyncRoutineReminders();
  };

  const anyOn = ROUTINE_SLOTS.some((slot) => reminders[slot].enabled);
  // Held as a key and resolved at render, so switching language re-translates it.
  const noticeKey =
    permission === "unsupported"
      ? "profile.reminders.permissionWeb"
      : permission === "denied" && anyOn
        ? "profile.notifications.permissionDenied"
        : null;

  const rowSx = {
    // `labelPlacement="start"` ships negative margins that would pull the text off
    // the page gutter; the row owns its own alignment instead.
    marginInline: 0,
    gap: 2,
    justifyContent: "space-between",
  } as const;

  return (
    <SettingsPage
      title={t("profile.reminders.title")}
      description={t("profile.reminders.description")}
      status={status}
      onDismiss={dismiss}
      backTo={fromRoutine ? ROUTINE_PATH : undefined}
      backLabel={fromRoutine ? t("routine.backToRoutine") : undefined}
    >
      {ROUTINE_SLOTS.map((slot) => (
        <Box
          key={slot}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          <FormControlLabel
            labelPlacement="start"
            disableTypography
            control={
              <Switch
                // The switch's ripple padding would otherwise stop the track 4px short
                // of the gutter — see the notification settings.
                sx={{ marginRight: "-12px" }}
                checked={reminders[slot].enabled}
                disabled={saving}
                onChange={(event) => void toggle(slot, event.target.checked)}
              />
            }
            label={
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography variant="T2">
                  {t(`profile.reminders.${slot}`)}
                </Typography>
                <Typography variant="T3" sx={{ color: "text.muted" }}>
                  {t("profile.reminders.preview", {
                    text: t(`routine.reminder.${slot}`),
                  })}
                </Typography>
              </Box>
            }
            sx={rowSx}
          />

          {reminders[slot].enabled ? (
            <ReminderTimeField
              label={t("profile.reminders.time")}
              value={reminders[slot].time}
              onCommit={(time) => save(reminderUpdate(slot, { time }))}
              testId={`reminders.time-${slot}`}
            />
          ) : null}
        </Box>
      ))}

      {noticeKey ? (
        <Alert severity="info" data-testid="reminders.permission">
          {t(noticeKey)}
        </Alert>
      ) : null}
    </SettingsPage>
  );
}
